<template>
  <div class="form-group">
    <label v-if="labelName" :for="inputId" class="form-label">
      {{ labelName }}
    </label>

    <div
      class="simplified-phone-input"
      :class="{ 'is-invalid': errorMessage, disabled: disabled }"
    >
      <div class="country-code-display">
        {{ countryDialCode }}
      </div>
      <input
        :id="inputId"
        ref="inputRef"
        :value="displayValue"
        type="tel"
        inputmode="tel"
        autocomplete="tel-national"
        class="phone-input"
        :placeholder="placeholder"
        :disabled="disabled"
        @input="handleInput"
        @blur="handleBlur"
      />
    </div>

    <div v-if="errorMessage" class="invalid-feedback d-block">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import useDialCode from "@/apps/merchant3/shared-hooks/useDialCode";

// Format phone number based on digit count (no country-specific logic)
const formatByDigitCount = (digits: string): string => {
  const len = digits.length;
  if (len === 8) {
    return `${digits.slice(0, 4)} ${digits.slice(4)}`;
  } else if (len === 9) {
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  } else if (len === 10) {
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  } else if (len === 11) {
    return `${digits.slice(0, 2)} ${digits.slice(2, 7)} ${digits.slice(7)}`;
  }
  return digits; // No formatting for other lengths
};

const props = defineProps({
  country: {
    type: String,
    required: true,
  },
  labelName: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "",
  },
  inputId: {
    type: String,
    default: "simplified-phone-input",
  },
  modelValue: {
    type: String,
    default: "",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["update:modelValue", "validate-emit", "blur"]);

const { t } = useI18n();
const { getDialCode } = useDialCode();
const inputRef = ref<HTMLInputElement | null>(null);
const phoneNumber = ref("");
const errorMessage = ref("");

const countryDialCode = computed(() => {
  return getDialCode(props.country);
});

// Raw digits only (no formatting)
const rawDigits = computed(() => {
  return phoneNumber.value.replace(/[^0-9]/g, "");
});

// Formatted display value
const displayValue = computed(() => {
  return formatByDigitCount(rawDigits.value);
});

const fullPhoneNumber = computed(() => {
  if (!rawDigits.value) return "";
  return `${countryDialCode.value}${rawDigits.value}`;
});

// Process modelValue to extract national number (without country code)
const processModelValue = (value: string | null | undefined) => {
  if (value) {
    // Get all digits from the value
    const allDigits = value.replace(/[^0-9]/g, "");
    const dialCodeDigits = countryDialCode.value.replace(/[^0-9]/g, "");

    // If the digits start with the country dial code, strip it
    if (allDigits.startsWith(dialCodeDigits)) {
      phoneNumber.value = allDigits.slice(dialCodeDigits.length);
    } else {
      phoneNumber.value = allDigits;
    }
  } else {
    phoneNumber.value = "";
  }
};

// Watch both modelValue and country - re-process when either changes
watch(
  [() => props.modelValue, () => props.country],
  ([newValue]) => {
    processModelValue(newValue);
    const digits = phoneNumber.value.replace(/[^0-9]/g, "");
    if (digits && digits.length >= 7 && digits.length <= 15) {
      errorMessage.value = "";
      emit("validate-emit", true);
    }
  },
  { immediate: true }
);

const isValidPhoneNumber = (digits: string): boolean => {
  if (!props.required && !digits) return true;
  return digits.length >= 7 && digits.length <= 15;
};

const validate = (): boolean => {
  if (!rawDigits.value && props.required) {
    errorMessage.value = t("SHARED.PHONE_NUMBER_ERROR_TEXT");
    emit("validate-emit", false);
    return false;
  }

  if (!isValidPhoneNumber(rawDigits.value)) {
    errorMessage.value = t("SHARED.PHONE_ERROR_TEXT_NOT_VALID");
    emit("validate-emit", false);
    return false;
  }

  errorMessage.value = "";
  emit("validate-emit", true);
  emit("update:modelValue", fullPhoneNumber.value);
  return true;
};

const handleInput = (event: Event) => {
  const input = event.target as HTMLInputElement;
  const rawValue = input.value;
  const digits = rawValue.replace(/[^0-9]/g, "");
  const dialCodeDigits = countryDialCode.value.replace(/[^0-9]/g, "");
  // Strip country dial code if present. Two cases:
  // 1. Input starts with "+" (value set programmatically, e.g. invoke("val", "+52..."))
  // 2. Multi-digit dial codes (2+ digits): also strip when digits start with the code,
  //    because cy.type("+52...") absorbs the "+" on first keystroke leaving just digits.
  //    Single-digit codes like US (+1) are excluded to avoid false positives.
  const shouldStrip =
    digits.startsWith(dialCodeDigits) &&
    digits.length > dialCodeDigits.length &&
    (rawValue.startsWith("+") || dialCodeDigits.length >= 2);
  if (shouldStrip) {
    phoneNumber.value = digits.slice(dialCodeDigits.length);
  } else {
    phoneNumber.value = digits;
  }
  errorMessage.value = "";
  emit("update:modelValue", fullPhoneNumber.value);
  // Emit validation status so parent can enable/disable buttons immediately
  const nationalDigits = phoneNumber.value.replace(/[^0-9]/g, "");
  emit("validate-emit", nationalDigits.length >= 7 && nationalDigits.length <= 15);
};

const handleBlur = (event: FocusEvent) => {
  validate();
  emit("blur", event);
};

const getInputFieldValue = () => {
  return fullPhoneNumber.value;
};

const focus = () => {
  inputRef.value?.focus();
};

defineExpose({
  validate,
  getInputFieldValue,
  focus,
});
</script>

<style lang="scss" scoped>
.simplified-phone-input {
  display: flex;
  align-items: center;
  border: 2px solid #bdb9c6;
  border-radius: 11px;
  height: 40px;
  overflow: hidden;
  background-color: #fff;

  &.is-invalid {
    border-color: #dc3545;
  }

  &.disabled {
    background-color: #e9ecef;
  }
}

.country-code-display {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  height: 100%;
  background-color: #f5f5f5;
  border-right: 1px solid #bdb9c6;
  color: #6c757d;
  font-weight: 500;
  min-width: 60px;
  user-select: none;
}

.phone-input {
  flex: 1;
  border: none;
  outline: none;
  padding: 0 12px;
  height: 100%;
  font-size: 1rem;

  &:disabled {
    background-color: #e9ecef;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #adb5bd;
  }
}

.invalid-feedback {
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}
</style>
